import {Howl} from "howler";
import * as PIXI from "pixi.js";
import {config} from "../config";
import {ApiService} from "./api/ApiService";
import data from "./configs/data";
import {FunModeFeatures} from "./FunModeFeatures/FunModeFeatures";
import {GameCameraAnimation} from "./GameCameraAnimation";
import {GoldenChips} from "./GoldenChips";
import {InfoModal} from "./InfoModal";
import {NumberFrameAnimation} from "./NumberFrameAnimation";
import {Paytable} from "./Paytable";
import {PixiApplication} from "./PixiApplication";
import {RouletteWheel} from "./RouletteWheel";
import {AutoPlayMenu} from "./userInterface/autoplayMenu/AutoPlayMenu";
import {IAutoPlayMenu} from "./userInterface/autoplayMenu/IAutoPlayMenu";
import {BetPicker} from "./userInterface/betPicker/BetPicker";
import {IBetPicker} from "./userInterface/betPicker/IBetPicker";
import {Footer} from "./userInterface/footer/Footer";
import {LastDrawnNumbersPanel} from "./userInterface/LastDrawnNumbersPanel";
import {RouletteBetsPanel} from "./userInterface/rouletteBetsPanel/implementations/RouletteBetsPanel";
import {RouletteButtonsPanel} from "./userInterface/RouletteButtonsPanel";
import {Utils} from "./Utils";
import {WinBanner} from "./WinBanner";

export class EuropeanRoulette extends PIXI.Container {

    public static readonly ROULETTE_MAXIMUM_NUMBER = 36;

    public static readonly RED_COLOR_NUMBERS: Array<number> =
        [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];

    private readonly BET_PICKER_VALUES = [0.1, 0.5, 1, 2.5, 5, 10];
    private readonly BET_PICKER_TEXTURES = [
        data.images.betButtonValue0_1,
        data.images.betButtonValue0_5,
        data.images.betButtonValue1,
        data.images.betButtonValue2_5,
        data.images.betButtonValue5,
        data.images.betButtonValue10,
    ];

    private readonly SMALL_BETS_BET_PICKER_VALUES = [0.01, 0.05, 0.1, 0.25, 0.5, 1];
    private readonly SMALL_BETS_BET_PICKER_TEXTURES = [
        data.images.smallBetsBetButtonValue001,
        data.images.smallBetsBetButtonValue005,
        data.images.smallBetsBetButtonValue01,
        data.images.smallBetsBetButtonValue025,
        data.images.smallBetsBetButtonValue05,
        data.images.smallBetsBetButtonValue1,
    ];

    private readonly EXTREMELY_SMALL_BETS_BET_PICKER_VALUES = [0.001, 0.005, 0.01, 0.025, 0.05, 0.1];
    private readonly EXTREMELY_SMALL_BETS_BET_PICKER_TEXTURES = [
        data.images.extremelySmallBetsBetButtonValue0001,
        data.images.extremelySmallBetsBetButtonValue0005,
        data.images.extremelySmallBetsBetButtonValue001,
        data.images.extremelySmallBetsBetButtonValue0025,
        data.images.extremelySmallBetsBetButtonValue005,
        data.images.extremelySmallBetsBetButtonValue01,
    ];

    private readonly BETS_PANEL_POSITION = new PIXI.Point(100, 139);
    private readonly BACKGROUND_POSITION = new PIXI.Point(0, -560);
    private readonly ROULETTE_WHEEL_POSITION = new PIXI.Point(640, -280);
    private readonly ROULETTE_WHEEL_QUICK_SPIN_POSITION = new PIXI.Point(640, 360);
    private readonly WIN_BANNER_POSITION = new PIXI.Point(194, 281);
    private readonly ROULETTE_BUTTONS_PANEL_POSITION = new PIXI.Point(0, 615);
    private readonly ROULETTE_WHEEL_SCALE = new PIXI.Point(0.78, 0.78);
    private readonly BET_PICKER_POSITION = new PIXI.Point(640, 717);
    private readonly AUTO_PLAY_MENU_POSITION = new PIXI.Point(640, 360);
    private readonly FOOTER_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, 707);
    private readonly LAST_DRAWN_NUMBERS_POSITION = new PIXI.Point(1103, 38);
    private readonly PAYTABLE_POSITION = new PIXI.Point(0, 0);
    private readonly NUMBER_FRAME_ANIMATION_POSITION = new PIXI.Point(100, 87);
    private readonly GOLDEN_CHIPS_POSITION = new PIXI.Point(PixiApplication.WIDTH / 2, 430);

    private allGameContainer: PIXI.Container = new PIXI.Container();
    private steemAccountName: string;
    private steemConnectToken: string;
    private keychainJWT: string;
    private rouletteButtonsPanel: RouletteButtonsPanel;
    private rouletteBetsPanel: RouletteBetsPanel;
    private lastDrawnNumbersPanel: LastDrawnNumbersPanel;
    private rouletteWheel: RouletteWheel;
    private betPicker: IBetPicker;
    private numberFrameAnimation: NumberFrameAnimation;
    private autoPlayMenu: IAutoPlayMenu;
    private winBanner: WinBanner;
    private goldenChips: GoldenChips;

    private gameCameraAnimation: GameCameraAnimation;
    private apiService = new ApiService();
    private connectionTimeOut: any;
    private showSlowConnectionModalAgain = true;
    private remainingAutoSpinsValue: number = 0;
    private backgroundThemeSound: Howl;
    private wheelResultSound: Howl;

    constructor() {
        super();

        this.createAndGameCameraAnimation();
        this.createAndAddBackground();
        this.createAndAddFrameAnimation();
        this.createAndAddBetsPanel();
        this.createAndAddRouletteBetsButtonsPanel();
        this.createAndAddLastDrawnNumbersPanel();
        this.createAndAddBetPicker();
        this.createAndRouletteWheel();
        this.createAndAddAutoPlayMenu();
        this.createAndAddWinBanner();
        this.createAndAddGoldenChips();
        this.createAndAddFooter();
        this.createAndAddPayTable();
        this.backgroundThemeSound = new Howl({
            src: [data.audios.backgroundTheme],
            volume: 0.7,
            loop: true,
        });
        this.backgroundThemeSound.play();
        this.wheelResultSound = new Howl({
            src: [data.audios.wheelResult],
        });
    }

    public setCredentials(steemAccountName: string, steemConnectToken: string, keychainJWT: string): void {
        this.steemAccountName = steemAccountName;
        this.steemConnectToken = steemConnectToken;
        this.keychainJWT = keychainJWT;
    }

    public async initGame(): Promise<any> {
        this.rouletteButtonsPanel.disableSpinButtons();
        const accountInfo = await this.apiService.initGame(
            this.steemAccountName, this.steemConnectToken, this.keychainJWT);
        Footer.getInstance().setBalanceValue(accountInfo.steemAmount);
        this.rouletteBetsPanel.setBalanceValue(accountInfo.steemAmount);
        this.rouletteButtonsPanel.enableSpinButtons();
    }

    private async playRound(reuseSameBet: boolean, quickSpin: boolean): Promise<void> {
        // disable proper things
        this.rouletteButtonsPanel.disableAllButtons();
        this.rouletteBetsPanel.disableBetPanel();
        this.goldenChips.visible = false;
        // ---------------------

        // wait for server response
        this.rouletteButtonsPanel.playLoadingAnimationOnSpinButton();
        if (this.showSlowConnectionModalAgain) {
            this.startSlowConnectionCountDownAndInitializeBehavior();
        }
        const serverResponse = await this.apiService.getGameResult(this.rouletteBetsPanel.getRouletteBets(),
            config.baseCurrency);
        if (this.showSlowConnectionModalAgain && serverResponse) {
            this.removeConnectionCountDown();
        }
        this.rouletteButtonsPanel.playIdleAnimationOnSpinButton();
        // --------------------------

        await this.animateWheelAndShowWin(serverResponse.rouletteResult.drawnNumber,
            serverResponse.rouletteResult.prize,
            quickSpin);

        if (serverResponse.rouletteResult.prize > 0) {
            this.goldenChips.visible = true;
            this.goldenChips.showChips(serverResponse.rouletteResult.prize);
        }

        this.numberFrameAnimation.highlightFrame(serverResponse.rouletteResult.drawnNumber);
        this.lastDrawnNumbersPanel.addLastDrawnNumber(serverResponse.rouletteResult.drawnNumber);

        Footer.getInstance().setBalanceValue(serverResponse.accountInfo.steemAmount);
        this.rouletteBetsPanel.setBalanceValue(serverResponse.accountInfo.steemAmount);
        Footer.getInstance().setLastWinAmount(serverResponse.rouletteResult.prize);

        this.rouletteButtonsPanel.enableAllButtons();
        this.rouletteBetsPanel.enableBetPanel();

        if (!reuseSameBet) {
            this.rouletteBetsPanel.clearRouletteBets(false);
        }

        // give player chance to stop auto play before moving to next round
        if (this.remainingAutoSpinsValue > 0) {
            await Utils.requestTimeout(1500);
        }

        FunModeFeatures.addSpin();

        if (this.remainingAutoSpinsValue > 0) {
            this.remainingAutoSpinsValue--;
            this.rouletteButtonsPanel.setOnSpinButtonSpinsLeft(this.remainingAutoSpinsValue);
            await this.playRound(reuseSameBet, quickSpin);
        }
    }

    private async animateWheelAndShowWin(drawnNumber: number, prize: number, quickSpin: boolean): Promise<any> {
        if (quickSpin) {
            this.rouletteWheel.position = this.ROULETTE_WHEEL_QUICK_SPIN_POSITION;
            this.wheelResultSound.play();
            await Promise.all([
                this.rouletteWheel.animateResult(drawnNumber, quickSpin),
                this.winBanner.show(drawnNumber, prize),
            ]);
            this.winBanner.hide();
        } else {
            this.rouletteWheel.position = this.ROULETTE_WHEEL_POSITION;
            await Promise.all([
                this.rouletteWheel.animateResult(drawnNumber, quickSpin),
                this.gameCameraAnimation.moveCameraOnRouletteWheel(),
            ]);

            this.winBanner.show(drawnNumber, prize);
            this.wheelResultSound.play();

            await Utils.requestTimeout(2000);

            this.winBanner.hide();
            await this.gameCameraAnimation.moveCameraOnGamePanel();
        }
    }

    private startSlowConnectionCountDownAndInitializeBehavior(): void {
        this.connectionTimeOut = setTimeout(() => {
            InfoModal.open("Internet connection problems.",
                "You lost you internet connection or your connection is slow.", () => {
                    this.showSlowConnectionModalAgain = false;
                }, true);
            InfoModal.setActionButtonText("Don't show again.");
        }, 5000);
    }

    private removeConnectionCountDown(): void {
        clearTimeout(this.connectionTimeOut);
    }

    private onSpinButtonClicked(): void {
        if (this.remainingAutoSpinsValue === 0) {
            this.playRound(true, false);
        } else {
            this.remainingAutoSpinsValue = 0;
            this.rouletteButtonsPanel.setOnSpinButtonSpinsLeft(this.remainingAutoSpinsValue);
        }
    }

    private onQuickSpinButtonClicked(): void {
        if (this.remainingAutoSpinsValue === 0) {
            this.playRound(true, true);
        } else {
            this.remainingAutoSpinsValue = 0;
            this.rouletteButtonsPanel.setOnSpinButtonSpinsLeft(this.remainingAutoSpinsValue);
        }
    }

    private createAndAddBackground(): void {
        const background: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.gameBackground);
        background.position = this.BACKGROUND_POSITION;
        this.allGameContainer.addChild(background);
    }

    private createAndAddLastDrawnNumbersPanel(): void {
        this.lastDrawnNumbersPanel = new LastDrawnNumbersPanel();
        this.lastDrawnNumbersPanel.position = this.LAST_DRAWN_NUMBERS_POSITION;
        this.allGameContainer.addChild(this.lastDrawnNumbersPanel);
    }

    private createAndAddBetsPanel(): void {
        this.rouletteBetsPanel = new RouletteBetsPanel();
        this.rouletteBetsPanel.position = this.BETS_PANEL_POSITION;
        this.allGameContainer.addChild(this.rouletteBetsPanel);
    }

    private createAndAddRouletteBetsButtonsPanel(): void {
        this.rouletteButtonsPanel = new RouletteButtonsPanel();
        this.rouletteButtonsPanel.position = this.ROULETTE_BUTTONS_PANEL_POSITION;
        this.rouletteButtonsPanel.subscribeOnRemoveAllBetsButtonClicked(() => {
            this.rouletteBetsPanel.clearRouletteBets();
        });
        this.rouletteButtonsPanel.subscribeOnRemoveBetButtonClicked(() => {
            this.rouletteBetsPanel.removeLastBet();
        });
        this.rouletteButtonsPanel.subscribeOnMenuButtonClicked(() => {
            this.autoPlayMenu.open();
        });
        this.rouletteButtonsPanel.subscribeOnSpinButtonClicked(() => {
            this.onSpinButtonClicked();
        });
        this.rouletteButtonsPanel.subscribeOnQuickSpinButtonClicked(() => {
            this.onQuickSpinButtonClicked();
        });
        this.rouletteButtonsPanel.subscribeOnRestoreBetsButtonClicked(() => {
            this.rouletteBetsPanel.restoreBets();
        });
        this.allGameContainer.addChild(this.rouletteButtonsPanel);
    }

    private createAndRouletteWheel(): void {
        this.rouletteWheel = new RouletteWheel();
        this.rouletteWheel.position = this.ROULETTE_WHEEL_POSITION;
        this.rouletteWheel.scale = this.ROULETTE_WHEEL_SCALE;
        this.allGameContainer.addChild(this.rouletteWheel);
    }

    private createAndAddBetPicker(): void {
        if (config.extremelySmallBets) {
            this.betPicker = new BetPicker(this.EXTREMELY_SMALL_BETS_BET_PICKER_TEXTURES,
                this.EXTREMELY_SMALL_BETS_BET_PICKER_VALUES);
        } else if (config.smallBets) {
            this.betPicker = new BetPicker(this.SMALL_BETS_BET_PICKER_TEXTURES, this.SMALL_BETS_BET_PICKER_VALUES);
        } else {
            this.betPicker = new BetPicker(this.BET_PICKER_TEXTURES, this.BET_PICKER_VALUES);
        }
        this.betPicker.position = this.BET_PICKER_POSITION;
        this.betPicker.subscribeOnBetChanged((value: number) => {
            this.rouletteBetsPanel.setBetValue(value);
        });
        this.rouletteBetsPanel.setBetValue(this.betPicker.getBetValue());
        this.allGameContainer.addChild(this.betPicker);
    }

    private createAndAddFrameAnimation(): void {
        this.numberFrameAnimation = new NumberFrameAnimation();
        this.numberFrameAnimation.position = this.NUMBER_FRAME_ANIMATION_POSITION;
        this.allGameContainer.addChild(this.numberFrameAnimation);
    }

    private createAndAddAutoPlayMenu(): void {
        this.autoPlayMenu = new AutoPlayMenu();
        this.autoPlayMenu.position = this.AUTO_PLAY_MENU_POSITION;
        this.autoPlayMenu.addOnSpinClickedListener((value: number, reuseSameBet: boolean, quickSpin: boolean) => {
            this.remainingAutoSpinsValue = value;
            this.playRound(reuseSameBet, quickSpin);
            this.remainingAutoSpinsValue--;
            this.rouletteButtonsPanel.setOnSpinButtonSpinsLeft(this.remainingAutoSpinsValue);
        });
        this.allGameContainer.addChild(this.autoPlayMenu);
    }

    private createAndAddWinBanner(): void {
        this.winBanner = new WinBanner();
        this.winBanner.position = this.WIN_BANNER_POSITION;
        this.addChild(this.winBanner);
    }

    private createAndGameCameraAnimation(): void {
        this.gameCameraAnimation = new GameCameraAnimation();
        this.allGameContainer.scale.y = -1;
        this.gameCameraAnimation.addToMainSlotContainer(this.allGameContainer);
        this.addChild(this.gameCameraAnimation);
    }

    private createAndAddFooter(): void {
        const footer = Footer.getInstance();
        footer.position = this.FOOTER_POSITION;
        this.addChild(footer);
    }

    private createAndAddGoldenChips(): void {
        this.goldenChips = new GoldenChips();
        this.goldenChips.position = this.GOLDEN_CHIPS_POSITION;
        this.addChild(this.goldenChips);
    }

    private createAndAddPayTable(): void {
        Paytable.getInstance().position = this.PAYTABLE_POSITION;
        this.addChild(Paytable.getInstance());
    }
}



// WEBPACK FOOTER //
// ./src/app/EuropeanRoulette.ts